/* eslint-disable complexity */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import style from "./styles";
import PremierAddToBagButton from "../PremierAddToBagButton/index.jsx";
import PremierBagErrorMessage from "../PremierBagErrorMessage/index.jsx";
import PremierFreeTrialErrorMessage from "../PremierFreeTrialError/index.jsx";
import AfterPayMessage from "../AfterPayMessage/index.jsx";
import getPremierAutoRenewalUrl from "../../utils/getPremierAutoRenewalUrl";
import trackAddToBag from "../../utils/trackAddToBag";
import store from "../../../app/store";
import { getSegmentsHeader } from "../../actions/PremierSelectorOptions";
import {
  setIncludeFreeTrials,
  setOfferedFreeTrial,
} from "../../actions/PremierPageFrame";
import { isAuthenticated } from "../../utils/identityUtils";
import { getCountryEndpoint } from "../../helpers";
import { fetchError } from "../../actions/CountrySelector";
import { isHighReturner } from "../../utils/isHighReturner";

const termsModalTextButton = React.createRef();

const getParam = (param) => {
  const match = RegExp(`[?&]${param}=([^&]*)`).exec(window.location.search);
  return match && !!decodeURIComponent(match[1].replace(/\+/g, " "));
};

const PremierProposition = ({
  appConfig,
  countryData,
  errorMessage,
  premierDeliveryProposition,
  assetsUrl,
  addPremierSubscription,
  openTermsAndConditions,
  premierSubscriptionAdded,
  offeredFreeTrial,
  firstName,
  includeFreeTrials,
}) => {
  const [displayFreeTrialError, setDisplayFreeTrialError] = useState(false);
  const [highReturner, setHighReturner] = useState(false);

  useEffect(() => {
    const location = window.location.href;
    setDisplayFreeTrialError(location.includes("signup-error"));
  }, []);

  useEffect(() => {
    if (offeredFreeTrial !== undefined) {
      setDisplayFreeTrialError(!offeredFreeTrial && includeFreeTrials);
    }
  }, [offeredFreeTrial, includeFreeTrials]);

  useEffect(() => {
    const checkCustomer = async () => {
      const result = await isHighReturner(appConfig);
      setHighReturner(result);
    };
    checkCustomer();
  }, [appConfig]);

  if (!countryData) return null;

  const supportsRecurring =
    countryData.supportedInstructionTypes.includes("Recurring");
  const myAccountUrl = getPremierAutoRenewalUrl(
    countryData.id,
    offeredFreeTrial,
    countryData.trialOffer?.id
  );
  const helpLink =
    "https://www.asos.com/customer-care/delivery/i-want-to-cancel-my-asos-premier-subscription-how-can-i-do-this/";
  const recurringInstructionStatement = countryData.messages.find(
    (message) =>
      message.subscriptionMessageType === "RecurringInstructionStatement"
  )?.message;

  const getTrialTitle = () => {
    return premierDeliveryProposition?.trialTitle
      .replace("%periodDays%", countryData.trialOffer.periodInDays)
      .replace("%firstName%", firstName);
  };

  const redirectToMyAccount = async (premierPrice, freeTrial) => {
    if (!freeTrial) {
      window.location.href = myAccountUrl;
      return;
    }
    const context = window?.asos && window.asos.webContext;

    const isLoggedIn = await isAuthenticated();

    const countryEndpoint = getCountryEndpoint(
      context.getBrowseCountry(),
      context.getBrowseCurrency(),
      context.getBrowseLanguage(),
      isLoggedIn
    );

    let additionalHeaders = {};

    if (isLoggedIn) {
      additionalHeaders = await getSegmentsHeader(additionalHeaders);
    }

    await fetch(countryEndpoint, {
      headers: additionalHeaders,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return store.dispatch(fetchError(response.message));
      })
      .then(async (data) => {
        const trialOfferExists = data?.subscriptionOptions[0]?.trialOffer;
        if (trialOfferExists) {
          await trackAddToBag(premierPrice, freeTrial);
          window.location.href = myAccountUrl;
        } else {
          setOfferedFreeTrial(false);
          setIncludeFreeTrials(false);
          window.location.href = `${window.location.pathname}#signup-error`;
        }
      })
      .catch((err) => {
        store.dispatch(fetchError(err.message));
      });
  };

  return (
    <div className={style.PremierDescription}>
      <div className={style.PremierProposition}>
        {offeredFreeTrial && (
          <p
            className={`${style.PremierDescriptionPrice} ${style.PremierDescriptionTrialTitle}`}
          >
            {getTrialTitle()}
          </p>
        )}
        <p className={style.PremierDescriptionText}>
          {
            countryData.messages.find(
              (message) => message.subscriptionMessageType === "Proposition"
            ).message
          }
        </p>

        {!offeredFreeTrial && (
          <p className={style.PremierDescriptionPrice}>
            {premierDeliveryProposition.priceText.replace(
              "%price%",
              countryData.price.text
            )}
          </p>
        )}

        {supportsRecurring && (
          <div className={style.PremierSignUp}>
            <button
              onClick={() =>
                redirectToMyAccount(countryData.price.text, offeredFreeTrial)
              }
              disabled={highReturner}
            >
              {offeredFreeTrial
                ? premierDeliveryProposition.joinTrialCtaButton.replace(
                    "%periodDays%",
                    countryData.trialOffer.periodInDays
                  )
                : premierDeliveryProposition.joinCtaButton}
            </button>
            <span>
              {premierDeliveryProposition.poweredByStripe}{" "}
              <img
                src={`${assetsUrl}/premier-landing-page/img/svg/stripe-logo.svg`}
                alt="Stripe"
              />
            </span>
          </div>
        )}

        {offeredFreeTrial && (
          <p className={style.PremierDescriptionPrice}>
            {premierDeliveryProposition.trialPriceText.replace(
              "%price%",
              countryData.price.text
            )}
          </p>
        )}

        {displayFreeTrialError && (
          <PremierFreeTrialErrorMessage
            onClose={setDisplayFreeTrialError}
            message={premierDeliveryProposition.trialError}
          />
        )}

        <AfterPayMessage
          premierDeliveryProposition={premierDeliveryProposition}
          assetsUrl={assetsUrl}
          premierPrice={countryData.price.value}
        />

        {supportsRecurring && (
          <>
            <p
              className={style.PremierRecurringInstructionStatement}
              dangerouslySetInnerHTML={{
                __html: recurringInstructionStatement,
              }}
            />
            <span
              className={style.PremierTermsAndConditions}
              dangerouslySetInnerHTML={{
                __html:
                  premierDeliveryProposition.cancellationTermsAndConditions.replace(
                    "{{1:helplink:string}}",
                    helpLink
                  ),
              }}
            />
          </>
        )}

        <p className={style.PremierTermsAndConditions}>
          {premierDeliveryProposition.termsAndConditions}
          <button
            onClick={() =>
              openTermsAndConditions(
                countryData.country.code,
                termsModalTextButton.current
              )
            }
            aria-label="ASOS Premier Terms and Conditions"
            ref={termsModalTextButton}
          >
            {premierDeliveryProposition.termsAndConditionsLinkText}
          </button>
        </p>

        {!supportsRecurring && (
          <PremierAddToBagButton
            premierPrice={countryData.price.text}
            subscriptionId={countryData.id}
            highReturner={highReturner}
            addPremierSubscription={addPremierSubscription}
            buttonText={premierDeliveryProposition.addSubscriptionButtonText}
            buttonTextAdded={
              premierDeliveryProposition.subscriptionAddedButtonText
            }
            premierSubscriptionAdded={premierSubscriptionAdded}
          />
        )}
      </div>

      {errorMessage ? (
        <PremierBagErrorMessage userMessage={errorMessage} />
      ) : null}

      {getParam("terms")
        ? openTermsAndConditions(
            countryData.country.code,
            termsModalTextButton.current
          )
        : null}
    </div>
  );
};

PremierProposition.propTypes = {
  appConfig: PropTypes.object,
  countryData: PropTypes.object,
  errorMessage: PropTypes.string,
  premierDeliveryProposition: PropTypes.object,
  assetsUrl: PropTypes.string,
  addPremierSubscription: PropTypes.func,
  openTermsAndConditions: PropTypes.func,
  premierSubscriptionAdded: PropTypes.bool,
  offeredFreeTrial: PropTypes.bool,
  firstName: PropTypes.string,
  includeFreeTrials: PropTypes.bool,
};

export default PremierProposition;
